/*
 * Utility functions for GalleryEditor
 */

import isVideo from 'shared/lib/isVideo';

/**
 * Returns indices of blocks eligible for merging
 *
 * @param id
 * @returns {Array}
 */
export const getMergableIndices = (id) => {
  const baseCase = [[id - 4], [id + 4]];

  let mergableIndices = [];

  switch (id % 4) {
    case 1:
      mergableIndices = baseCase.concat([
        [id - 1],
        [id + 1],
        [id + 5, id + 1, id + 4],
        [id - 5, id - 1, id - 4],
        [id - 4, id - 3, id + 1],
        [id - 1, id + 4, id + 3]
      ]);
      break;
    case 2:
      mergableIndices = baseCase.concat([
        [id - 1],
        [id + 1],
        [id + 5, id + 1, id + 4],
        [id - 5, id - 1, id - 4],
        [id - 4, id - 3, id + 1],
        [id - 1, id + 4, id + 3]
      ]);
      break;
    case 3:
      mergableIndices = baseCase.concat([
        [id - 1],
        [id - 5, id - 1, id - 4],
        [id - 1, id + 4, id + 3]
      ]);
      break;
    case 0:
      mergableIndices = baseCase.concat([
        [id + 1],
        [id + 5, id + 1, id + 4],
        [id - 4, id - 3, id + 1]
      ]);
      break;
    default:
      break;
  }
  return mergableIndices;
};

export const calculateNumberOfBlocks = (draft) => {
  const items = draft.slice();

  if (items === null || items.length === 0) {
    return 0;
  }

  return Math.max(...items.map(item => Math.max(...item.members))) + 1;
};

/*
  Here, the calculation/implementation of positioning images in the grid is exactly
  the same as on the frontend (shared/dynamic-components/WaterfallGallery/WaterfallGallery.jsx).
  By keep them consistent, we can make sure as long as the data can be rendered correctly
  here, it will work fine on the frontend.
*/
export const getImageStyle = (size, imgUrl, id, containerWidth) => {
  const unitSize = Math.floor(containerWidth / 4);
  const single = unitSize.toString();
  const double = (unitSize * 2).toString();
  const top = (Math.floor(id / 4) * unitSize).toString();
  const left = ((id % 4) * unitSize).toString();

  let style = {};

  switch (size) {
    case 'SMALL':
      style = {
        width: `${single}px`,
        height: `${single}px`
      };
      break;
    case 'VERTICAL_MEDIUM':
      style = {
        width: `${single}px`,
        height: `${double}px`
      };
      break;
    case 'HORIZONTAL_MEDIUM':
      style = {
        width: `${double}px`,
        height: `${single}px`
      };
      break;
    case 'LARGE':
      style = {
        width: `${double}px`,
        height: `${double}px`
      };
      break;
    default:
      break;
  }
  style = {
    ...style,
    left: `${left}px`,
    top: `${top}px`
  };
  const backgroundStyle = isVideo(imgUrl) ? {} : { backgroundImage: `url(${imgUrl})` };

  if (imgUrl) {
    style = {
      ...style,
      ...backgroundStyle
    };
  }
  return style;
};

export const handleMediumMerge = (res, mergeFromId, mergeToId) => {
  const itemIndex = res.findIndex(item => item.id === mergeToId);
  const itemIndexToRemove = res.findIndex(item => item.id === mergeFromId);

  const mergeObject = {
    size: (mergeToId - mergeFromId === 1) ? 'HORIZONTAL_MEDIUM' : 'VERTICAL_MEDIUM',
    url: res[itemIndex].url ? res[itemIndex].url : res[itemIndexToRemove].url,
    coverUrl: res[itemIndex].coverUrl ? res[itemIndex].coverUrl : res[itemIndexToRemove].coverUrl,
    socialUrl: res[itemIndex].socialUrl ? res[itemIndex].socialUrl : res[itemIndexToRemove].socialUrl,
    title: res[itemIndex].title ? res[itemIndex].title : res[itemIndexToRemove].title,
    titleColor: res[itemIndex].titleColor ? res[itemIndex].titleColor : res[itemIndexToRemove].titleColor,
  };

  res.splice(itemIndex, 1, {
    id: mergeFromId,
    members: [mergeFromId, mergeToId],
    ...mergeObject
  });
  res.splice(itemIndexToRemove, 1);

  return res;
};

export const handleLargeMerge = (res, mergeFromId, mergeToId) => {
  let blockNW = mergeFromId;

  let blockSE = mergeToId;

  let blockNE = mergeFromId;

  let blockSW = mergeToId;

  if (mergeToId - mergeFromId === 3) {
    blockNW = mergeFromId - 1;
    blockSE = mergeToId + 1;
  }
  if (mergeToId - mergeFromId === 5) {
    blockNE = mergeFromId + 1;
    blockSW = mergeToId - 1;
  }

  const itemNWIndex = res.findIndex(item => item.id === blockNW);
  const itemSEIndex = res.findIndex(item => item.id === blockSE);
  const itemNEIndex = res.findIndex(item => item.id === blockNE);
  const itemSWIndex = res.findIndex(item => item.id === blockSW);

  const mergeObject = {
    url: res[itemNWIndex].url || res[itemSEIndex].url || res[itemNEIndex].url || res[itemSWIndex].url || '',
    coverUrl: res[itemNWIndex].coverUrl || res[itemSEIndex].coverUrl || res[itemNEIndex].coverUrl || res[itemSWIndex].coverUrl || '',
    socialUrl: res[itemNWIndex].socialUrl || res[itemSEIndex].socialUrl || res[itemNEIndex].socialUrl || res[itemSWIndex].socialUrl || '',
    title: res[itemNWIndex].title || res[itemSEIndex].title || res[itemNEIndex].title || res[itemSWIndex].title || '',
    titleColor: res[itemNWIndex].titleColor || res[itemSEIndex].titleColor || res[itemNEIndex].titleColor || res[itemSWIndex].titleColor || '',
    size: 'LARGE'
  };

  res.splice(itemNWIndex, 1, {
    id: blockNW,
    members: [blockNW, blockSE, blockNE, blockSW],
    ...mergeObject
  });

  return res.filter(item => ![blockSE, blockNE, blockSW].includes(item.id));
};
